import { useRolodexEntries } from 'features/rolodex'
import { useMemo } from 'react'
import { RolodexEntryType } from 'types'

/**
 * Hook to check if any asset-lens related data exists for a unit
 * Returns true if any asset-lens data is found, false otherwise
 */
export function useHasAssetLensData(unitID: string | undefined): {
  hasData: boolean
  isLoading: boolean
} {
  // Query for sensor data with limit 1 - most common asset-lens data type
  const sensorDataQuery = useMemo(
    () =>
      unitID
        ? {
            type: RolodexEntryType.DATA_POINTS,
            tags: { unit: unitID },
            limit: 1, // Only need to know if any exists
          }
        : undefined,
    [unitID]
  )

  const { entries: sensorEntries, isLoading: isSensorLoading } =
    useRolodexEntries(sensorDataQuery)

  // Check for work orders as fallback - only query if no sensor data found
  const shouldCheckWorkOrders =
    unitID && !isSensorLoading && (!sensorEntries || sensorEntries.length === 0)

  const workOrderQuery = useMemo(
    () =>
      shouldCheckWorkOrders
        ? {
            type: RolodexEntryType.ASSET_LENS_WORK_ORDER,
            tags: { unit: unitID },
            limit: 1,
          }
        : undefined,
    [shouldCheckWorkOrders, unitID]
  )

  const { entries: workOrderEntries, isLoading: isWorkOrderLoading } =
    useRolodexEntries(workOrderQuery)

  // Determine if we have any data
  const hasData =
    (sensorEntries && sensorEntries.length > 0) ||
    (workOrderEntries && workOrderEntries.length > 0)

  const isLoading =
    isSensorLoading || (shouldCheckWorkOrders && isWorkOrderLoading) || false

  return { hasData, isLoading }
}
